import { favoritesRemoveErrorDescriptionText } from 'owa-locstrings/lib/strings/favoritesremoveerrordescriptiontext.locstring.json';
import { favoritesRemoveErrorTitleText } from 'owa-locstrings/lib/strings/favoritesremoveerrortitletext.locstring.json';
import { favoritesErrorDescriptionText } from 'owa-locstrings/lib/strings/favoriteserrordescriptiontext.locstring.json';
import { favoritesErrorTitleText } from 'owa-locstrings/lib/strings/favoriteserrortitletext.locstring.json';
import { CancelText } from 'owa-locstrings/lib/strings/canceltext.locstring.json';
import loc from 'owa-localize';
import {
    addNotification,
    FavoriteEntity,
    FavoritePerson,
    FavoritePrivateDistributionList,
    createDataActionFactory,
    Feature,
    createActionFactory,
    PersonType,
    sequentiallyProcessPromiseFactories,
    ThunkDispatch,
    runWithQoS,
    FavoriteUnifiedGroup,
    hashFolderId,
} from '../../../shared';
import { favoriteGroupWithCommonFavoritesAPIErrorNotification } from '../user/groups/notificationBarActions';
import { isFeatureEnabled } from 'owa-feature-flags';
import type {
    OutlookFavoriteServiceDataType,
    FavoritePersonaNode,
    FavoriteOperationError,
} from 'owa-favorites-types';

const getOwaMailFavoritesStoreModule = () =>
    import(/* webpackChunkName: "OwaMailFavoritesStore" */ 'owa-mail-favorites-store');
const getOwaFavoritesModule = () => import(/* webpackChunkName: "OwaFavorites" */ 'owa-favorites');

export const addFavoritePersonaStartedAction = createActionFactory<{
    favorite: FavoriteEntity;
}>('AddFavoritePersonaStartedAction', Feature.Favorite, action => {
    const entity = action.payload.favorite;
    const isPerson = entity.type === PersonType.FavoritePerson;
    let favoriteKind;
    switch (entity.type) {
        case PersonType.FavoritePerson:
            favoriteKind = 'FavoritePerson';
            break;
        case PersonType.FavoritePrivateDistributionList:
            favoriteKind = 'FavoritePdl';
            break;
        case PersonType.FavoriteUnifiedGroup:
            favoriteKind = 'FavoriteUnifiedGroup';
            break;
        default:
            favoriteKind = 'UnsupportedPersona';
            break;
    }
    return {
        favoriteKind,
        hasPersonaId: isPerson && !!(entity as FavoritePerson).personaId,
    };
});

export const addFavoritePersonaCompletedAction = createDataActionFactory<
    { hubPersonaId: string; newFavoriteId: string; tempFavoriteId: string },
    { error: FavoriteOperationError; hubPersonaId: string; favoriteId: string }
>('AddFavoritePersonaCompletedAction', Feature.Favorite, action => ({
    favoriteId: action.payload.newFavoriteId,
}));

export const removeFavoritePersonaStartedAction = createActionFactory<{
    hubPersonaId: string;
    favoriteId: string;
    favoriteType: PersonType;
}>('RemoveFavoritePersonaStartedAction', Feature.Favorite, action => {
    let favoriteKind;
    switch (action.payload.favoriteType) {
        case PersonType.Person:
        case PersonType.FavoritePerson:
            favoriteKind = 'FavoritePerson';
            break;
        case PersonType.GroupBasicData:
        case PersonType.Group:
        case PersonType.FavoriteUnifiedGroup:
            favoriteKind = 'FavoriteUnifiedGroup';
            break;
        case PersonType.PrivateDistributionList:
        case PersonType.FavoritePrivateDistributionList:
            favoriteKind = 'FavoritePdl';
            break;
        default:
            favoriteKind = 'UnsupportedPersona';
            break;
    }
    return {
        favoriteKind,
        favoriteId: action.payload.favoriteId,
    };
});

export const removeFavoritePersonaCompletedAction = createDataActionFactory<
    { hubPersonaId: string; favoriteId: string },
    {
        error: FavoriteOperationError;
        hubPersonaId: string;
        favoriteId: string;
        favoriteType: PersonType;
    }
>(
    'RemoveFavoritePersonaCompletedAction',
    Feature.Favorite,
    action => ({
        favoriteId: action.payload.favoriteId,
    }),
    action => ({
        favoriteId: action.payload.favoriteId,
        favoriteType: action.payload.favoriteType,
    })
);

export const removeFavoriteFolderStartedAction = createActionFactory<{
    favoriteId: string;
}>('RemoveFavoriteFolderStartedAction', Feature.Favorite, action => ({
    favoriteId: action.payload.favoriteId,
}));

export const removeFavoriteFolderCompletedAction = createDataActionFactory<
    { favoriteId: string; folderId: string },
    {
        error: FavoriteOperationError;
        favoriteId: string;
        folderId: string;
        favoriteType: string;
    }
>(
    'RemoveFavoriteFolderCompletedAction',
    Feature.Favorite,
    action => ({
        favoriteId: action.payload.favoriteId,
        folderId: hashFolderId(action.payload.folderId),
    }),
    action => ({
        favoriteId: action.payload.favoriteId,
        folderId: hashFolderId(action.payload.folderId),
        favoriteType: action.payload.favoriteType,
    })
);

export const editFavoritePersonaStartedAction = createActionFactory<{
    favoriteId: string;
    updatedFavorite: Partial<FavoriteEntity>;
}>('EditFavoritePersonaStartedAction', Feature.Favorite, action => ({
    favoriteId: action.payload.favoriteId,
}));

export const editFavoritePersonaCompletedAction = createDataActionFactory<
    { favoriteId: string },
    {
        error: FavoriteOperationError;
        favoriteId: string;
        favoriteType: PersonType;
    }
>(
    'EditFavoritePersonaCompletedAction',
    Feature.Favorite,
    action => ({
        favoriteId: action.payload.favoriteId,
    }),
    action => ({
        favoriteId: action.payload.favoriteId,
        favoriteType: action.payload.favoriteType,
    })
);

export const addFavoritePersona = (favorite: FavoriteEntity, isRetry?: boolean) => (
    dispatch: ThunkDispatch
) => {
    dispatch(addFavoritePersonaStartedAction({ favorite: favorite }));

    runWithQoS({
        scenarioName: isFeatureEnabled('tri-favorites-roaming')
            ? 'AddRoamingFavorite'
            : 'AddFavorite',
        feature: Feature.Favorite,
        operation: getAddFavoritePromise(favorite),
        additionalQoSLogProperty: {
            favoriteKind: favorite.type,
        },
    })
        .then((favoriteId: string) => {
            dispatch(
                addFavoritePersonaCompletedAction.success({
                    hubPersonaId: favorite.hubPersonaId,
                    newFavoriteId: favoriteId,
                    tempFavoriteId: favorite.favoriteId,
                })
            );
        })
        .catch((e: FavoriteOperationError) => {
            dispatch(
                addFavoritePersonaCompletedAction.error({
                    error: e,
                    hubPersonaId: favorite.hubPersonaId,
                    favoriteId: favorite.favoriteId,
                })
            );
            if (favorite.type === PersonType.FavoriteUnifiedGroup) {
                favoriteGroupWithCommonFavoritesAPIErrorNotification(dispatch)(
                    favorite.hubPersonaId,
                    favorite.info.displayName,
                    false,
                    isRetry,
                    favorite.favoriteId,
                    favorite
                );
            } else {
                addNotification(
                    loc(favoritesErrorTitleText),
                    loc(favoritesErrorDescriptionText),
                    loc(CancelText)
                );
            }
        });
};

async function getAddFavoritePromise(favorite: FavoriteEntity): Promise<string> {
    if (isFeatureEnabled('tri-favorites-roaming')) {
        const favoritesModule = await getOwaFavoritesModule();

        let outlookServiceData;
        if (favorite.type === PersonType.FavoritePerson) {
            outlookServiceData = createAddFavoritePersonaServicePayload(
                favorite,
                favoritesModule.convertEWSFolderIdToRestFolderId
            );
        } else if (favorite.type === PersonType.FavoritePrivateDistributionList) {
            outlookServiceData = createAddFavoritePdlServicePayload(
                favorite,
                favoritesModule.convertEWSFolderIdToRestFolderId
            );
        } else if (favorite.type === PersonType.FavoriteUnifiedGroup) {
            outlookServiceData = createAddFavoriteGroupServicePayload(
                favorite,
                favoritesModule.convertEWSFolderIdToRestFolderId
            );
        }

        const response = (await favoritesModule.lazyCreateOutlookFavoriteService.importAndExecute(
            outlookServiceData
        )) as OutlookFavoriteServiceDataType;
        return response.Id;
    } else {
        if (
            favorite.type === PersonType.FavoritePrivateDistributionList ||
            favorite.type === PersonType.FavoriteUnifiedGroup
        ) {
            throw new Error(
                'PDL and Unified Group favorites are only supported with Roaming favorites'
            );
        }

        const module = await getOwaMailFavoritesStoreModule();
        const favoriteNode = (await module.lazyAddFavoritePersona.importAndExecute(
            favorite.personaId,
            favorite.info.mainEmailAddress,
            favorite.info.displayName,
            'PeopleHub'
        )) as FavoritePersonaNode;
        return favoriteNode.id;
    }
}

/**
 * Remove the given HubPerson from favorites.
 * Will call the shared lazyToggleFavoritePersona method, which will take care of updating search folders and user config
 * @param entityToRemove the HubPerson to remove
 */
export const removeFavoritePersona = (
    hubPersonaId: string,
    favoriteId: string,
    displayName: string,
    favoriteType: PersonType,
    isRetry?: boolean
) => (dispatch: ThunkDispatch) => {
    dispatch(removeFavoritePersonaStartedAction({ hubPersonaId, favoriteId, favoriteType }));

    return runWithQoS({
        scenarioName: isFeatureEnabled('tri-favorites-roaming')
            ? 'RemoveRoamingFavorite'
            : 'RemoveFavorite',
        feature: Feature.Favorite,
        operation: getRemoveFavoritePromise(favoriteId),
        additionalQoSLogProperty: {
            favoriteKind: favoriteType.toString(),
            favoriteId: favoriteId,
        },
    })
        .then(() => {
            dispatch(
                removeFavoritePersonaCompletedAction.success({
                    hubPersonaId: hubPersonaId,
                    favoriteId: favoriteId,
                })
            );
        })
        .catch((e: FavoriteOperationError) => {
            dispatch(
                removeFavoritePersonaCompletedAction.error({
                    error: e,
                    hubPersonaId: hubPersonaId,
                    favoriteId: favoriteId,
                    favoriteType: favoriteType,
                })
            );
            const isGroup: boolean =
                favoriteType === PersonType.Group ||
                favoriteType === PersonType.GroupBasicData ||
                favoriteType === PersonType.FavoriteUnifiedGroup;

            if (isGroup) {
                favoriteGroupWithCommonFavoritesAPIErrorNotification(dispatch)(
                    hubPersonaId,
                    displayName,
                    true,
                    isRetry,
                    favoriteId
                );
            } else if (e.statusCode !== 404) {
                addNotification(
                    loc(favoritesRemoveErrorTitleText),
                    loc(favoritesRemoveErrorDescriptionText),
                    loc(CancelText)
                );
            }
        });
};

async function getRemoveFavoritePromise(favoriteIdToRemove: string): Promise<void> {
    if (isFeatureEnabled('tri-favorites-roaming')) {
        const favoritesModule = await getOwaFavoritesModule();
        await favoritesModule.lazyDeleteOutlookFavoriteService.importAndExecute(favoriteIdToRemove);
    } else {
        const favoritesStoreModule = await getOwaMailFavoritesStoreModule();
        await favoritesStoreModule.lazyRemoveFavoritePersona.importAndExecute(
            favoriteIdToRemove,
            'PeopleHub'
        );
    }
}

/**
 * Edit the given HubPerson In Favorites.
 * @param updatedFavorite the new information about favorites
 */
export const editFavoritePersona = (
    favoriteId: string,
    updatedFavorite: Partial<FavoriteEntity>
) => (dispatch: ThunkDispatch) => {
    dispatch(editFavoritePersonaStartedAction({ favoriteId, updatedFavorite }));

    return runWithQoS({
        scenarioName: 'EditRoamingFavorite',
        feature: Feature.Favorite,
        operation: getEditFavoritePromise(favoriteId, updatedFavorite),
        additionalQoSLogProperty: {
            favoriteKind: updatedFavorite.type.toString(),
            favoriteId: favoriteId,
        },
    })
        .then(() => {
            dispatch(
                editFavoritePersonaCompletedAction.success({
                    favoriteId: favoriteId,
                })
            );
        })
        .catch((e: FavoriteOperationError) => {
            dispatch(
                editFavoritePersonaCompletedAction.error({
                    error: e,
                    favoriteId: favoriteId,
                    favoriteType: updatedFavorite.type,
                })
            );
        });
};

async function getEditFavoritePromise(
    favoriteId: string,
    updatedFavorite: Partial<FavoriteEntity>
): Promise<void> {
    const editFavoritePayload = createEditFavoritePersonaPayload(updatedFavorite);
    const favoritesModule = await getOwaFavoritesModule();
    await favoritesModule.lazyEditOutlookFavoriteService.importAndExecute(
        favoriteId,
        editFavoritePayload
    );
}

function createAddFavoritePersonaServicePayload(
    favorite: FavoritePerson,
    convertIdFunction: (id: string) => string
): OutlookFavoriteServiceDataType {
    const dataForAdd = favorite.personaId
        ? {
              Key: 'PersonaId',
              Value: convertIdFunction(favorite.personaId),
          }
        : {
              Key: 'MainEmailAddress',
              Value: favorite.info.mainEmailAddress,
          };

    return {
        Type: 'persona',
        DisplayName: favorite.info.displayName,
        SingleValueSettings: [dataForAdd],
        Client: 'PeopleHub',
    };
}

function createAddFavoritePdlServicePayload(
    favorite: FavoritePrivateDistributionList,
    convertIdFunction: (id: string) => string
): OutlookFavoriteServiceDataType {
    return {
        Type: 'privatedistributionlist',
        DisplayName: favorite.info.displayName,
        SingleValueSettings: [
            {
                Key: 'PdlId',
                Value: convertIdFunction(favorite.info.itemId),
            },
        ],
        Client: 'PeopleHub',
    };
}

function createAddFavoriteGroupServicePayload(
    favorite: FavoriteUnifiedGroup,
    convertIdFunction: (id: string) => string
): OutlookFavoriteServiceDataType {
    return {
        Type: 'group',
        DisplayName: favorite.info.displayName,
        SingleValueSettings: [
            {
                Key: 'EmailAddress',
                Value: convertIdFunction(favorite.info.emailAddress),
            },
        ],
        Client: 'PeopleHub',
    };
}

function createEditFavoritePersonaPayload(
    updatedFavorite: Partial<FavoriteEntity>
): OutlookFavoriteServiceDataType {
    switch (updatedFavorite.type) {
        case PersonType.FavoritePrivateDistributionList:
            return createEditPdlPayload(
                updatedFavorite as Partial<FavoritePrivateDistributionList>
            );
        default:
            throw new Error('Edit is currently only supported for PDLs');
    }
}

function createEditPdlPayload(
    updatedPdlData: Partial<FavoritePrivateDistributionList>
): OutlookFavoriteServiceDataType {
    const dataObject: OutlookFavoriteServiceDataType = {
        Type: 'privatedistributionlist',
        Client: 'PeopleHub',
    };

    const { displayName, members } = updatedPdlData.info;

    if (displayName) {
        dataObject['DisplayName'] = displayName;
    }

    if (members) {
        const settings = [
            {
                Key: 'EmailAddresses',
                Values: members.map(member => member.emailAddressWrapper.EmailAddress),
            },
            {
                Key: 'MemberNames',
                Values: members.map(member => member.displayName),
            },
        ];

        dataObject['MultiValueSettings'] = settings;
    }

    return dataObject;
}

// If we are mutating the userConfiguration multiple times for multiple favorites, we need to mutate sequentially to keep state consistent
export const unfavoriteMultiplePersonas = (
    deletedFavorites: FavoriteEntity[],
    dispatch: ThunkDispatch
): Promise<void> =>
    sequentiallyProcessPromiseFactories(
        deletedFavorites.map(favorite => () =>
            dispatch(
                removeFavoritePersona(
                    favorite.hubPersonaId,
                    favorite.favoriteId,
                    favorite.info.displayName,
                    favorite.type
                )
            )
        )
    );
export type { Action } from '../../../shared';
export type { DataActionFactory } from '../../../shared';
